<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogScrollContent
      class="ai-check-dialog-content max-w-6xl w-full h-[calc(100vh-1.5rem)] sm:h-[calc(100vh-4rem)] p-0 gap-0 overflow-hidden flex flex-col sm:rounded rounded-none border border-solid border-border"
    >
      <AiCheckDialogHeader
        :case-id="data.caseId"
        :state="state"
        :progress="progress"
        :doc-count="data.documents.length"
      />

      <!-- Body — scrollable region -->
      <div class="flex-1 overflow-y-auto bg-muted/30">
        <div
          class="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4 lg:p-5 items-start"
        >
          <!-- Graph: full width across all columns -->
          <AiCheckGraph
            class="lg:col-span-3"
            :nodes="data.graphNodes"
            :edges="data.graphEdges"
            :state="state"
            :active-doc-id="activeDocId"
          />
          <!-- Documents: 2/3 width -->
          <AiCheckDocumentList
            class="lg:col-span-2 order-2 lg:order-none"
            :documents="data.documents"
            :state="state"
          />
          <!-- Right: Conclusion + Stats — 1/3 width -->
          <div
            class="flex flex-col gap-3 sm:gap-4 lg:col-span-1 order-1 lg:order-none"
          >
            <AiCheckConclusion
              :created-at="data.createdAt"
              :last-checked-at="data.lastCheckedAt"
              :status="data.status"
              :summary="data.summary"
              :confidence="data.score.confidence"
              :refusal-prob="data.score.refusalProb"
              :signals="data.signals"
              :state="state"
              :has-ai-result="data.hasAiResult"
              :verdict-status="data.verdictStatus"
              :is-stale="isStale"
              :new-docs-count="newDocsCount"
              :enable-typewriter="true"
              @regenerate="$emit('restart')"
            />
            <AiCheckStats
              v-if="state === 'done' || state === 'stale'"
              :confidence="data.score.confidence"
              :refusal-prob="data.score.refusalProb"
              :enabled="state === 'done' || state === 'stale'"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <AiCheckDialogFooter
        :state="state"
        :progress="progress"
        :refusal-prob="data.score.refusalProb"
        @start="$emit('start')"
        @cancel="$emit('cancel')"
        @restart="$emit('restart')"
        @ignore="$emit('ignore')"
        @fix="$emit('fix')"
      />
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { Dialog, DialogScrollContent } from "@/shared/ui/dialog";
import AiCheckGraph from "./AiCheckGraph.vue";
import AiCheckDocumentList from "./AiCheckDocumentList.vue";
import AiCheckConclusion from "./AiCheckConclusion.vue";
import AiCheckStats from "./AiCheckStats.vue";
import AiCheckDialogHeader from "./AiCheckDialogHeader.vue";
import AiCheckDialogFooter from "./AiCheckDialogFooter.vue";
import { computed } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  data: { type: Object, required: true },
  state: { type: String, default: "done" },
  progress: { type: Number, default: 0 },
  feedEvents: { type: Array, default: () => [] },
  isStale: { type: Boolean, default: false },
  newDocsCount: { type: Number, default: 0 },
});

const activeDocId = computed(() => {
  for (let i = props.feedEvents.length - 1; i >= 0; i--) {
    const ev = props.feedEvents[i];
    if (ev?.docId) {
      return ev.docId.startsWith("doc-") ? ev.docId.slice(4) : ev.docId;
    }
  }
  return null;
});

defineEmits(["update:open", "start", "cancel", "restart", "ignore", "fix"]);
</script>

<style>
/* Снимаем backdrop-blur с overlay'я только этого диалога.
 * backdrop-filter создаёт composited stacking context → Chrome
 * растеризует child-текст через GPU и получается blur (особенно
 * заметно на широких viewport). :has() выбирает прямого родителя
 * нашего DialogContent — это и есть DialogOverlay. */
:has(> .ai-check-dialog-content) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}
</style>
